import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Search, Compass } from "lucide-react";

/**
 * NotFound — replaces the previous "silently render Home" catch-all so
 * mistyped URLs (especially broken passport share links) show an honest
 * 404 with recovery links instead of pretending everything is fine.
 */
export default function NotFound() {
  const loc = useLocation();
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 bg-gradient-to-br from-white via-emerald-50/30 to-white"
      data-testid="not-found-page"
    >
      <div className="max-w-lg w-full border border-navy/10 rounded-2xl bg-white shadow-[0_20px_60px_rgba(15,61,46,0.10)] p-8">
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 mb-2">
          VenturaZ · 404
        </div>
        <div className="font-heading text-3xl text-ink leading-tight">
          We couldn&apos;t find that page.
        </div>
        <div className="text-sm text-ink-soft mt-3">
          The URL <code className="bg-navy/[0.04] px-1 py-0.5 rounded text-[11px] font-mono">{loc.pathname}</code>{" "}
          doesn&apos;t match any route on VenturaZ. It might have been mistyped, or the
          link you followed is broken or expired.
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Link
            to="/"
            className="px-3 py-2 rounded-md bg-navy text-white text-sm font-heading hover:bg-[#256E58] transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid="not-found-home"
          >
            <ArrowLeft size={13} /> Home
          </Link>
          <Link
            to="/marketplace"
            className="px-3 py-2 rounded-md bg-white text-ink border border-navy/15 text-sm font-heading hover:border-gold/50 hover:bg-gold/5 transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid="not-found-marketplace"
          >
            <Compass size={13} /> Marketplace
          </Link>
          <Link
            to="/login"
            className="px-3 py-2 rounded-md bg-white text-ink border border-navy/15 text-sm font-heading hover:border-gold/50 hover:bg-gold/5 transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid="not-found-login"
          >
            <Search size={13} /> Sign in
          </Link>
        </div>

        <div className="mt-6 text-[11px] text-ink-muted italic">
          Was this a passport share link? Double-check the full URL includes{" "}
          <code className="font-mono">/passport/share/&lt;token&gt;</code>.
        </div>
      </div>
    </div>
  );
}
